import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  ShieldCheck,
  ClipboardList,
  Thermometer,
  AlertTriangle,
  CalendarDays,
  Gift,
  CheckCircle2,
  ArrowRight,
  Sparkles,
  Star,
  MapPin,
  Phone,
  Mail,
  Building2,
  UserRound,
  FileText,
  Utensils,
  Hotel,
  Wine,
  ShoppingBasket,
  ChefHat,
  Loader2,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PROBLEMS = [
  { icon: FileText, text: "Manuale HACCP non aggiornato" },
  { icon: AlertTriangle, text: "Attestati alimentaristi scaduti" },
  { icon: Utensils, text: "Allergeni non gestiti correttamente" },
  { icon: Thermometer, text: "Registri temperature incompleti" },
  { icon: ClipboardList, text: "Piano pulizie non applicato" },
  { icon: ShieldCheck, text: "Documenti non coerenti con l'attività reale" },
];

const INCLUDED = [
  "Verifica del Manuale HACCP",
  "Gestione degli allergeni",
  "Formazione degli alimentaristi",
  "Registrazioni obbligatorie",
  "Scadenzario degli obblighi",
  "Sicurezza sul lavoro (D.Lgs. 81/08)",
  "Obblighi per hotel, agriturismi e strutture ricettive",
];

const GIFT_ITEMS = [
  { icon: ClipboardList, text: "Checklist pre-controllo" },
  { icon: Thermometer, text: "Esempio registro temperature" },
  { icon: Utensils, text: "Mini guida allergeni" },
  { icon: CalendarDays, text: "Calendario scadenze 2026" },
  { icon: ShieldCheck, text: "Schema: cosa verificare prima di un'ispezione" },
];

const REVIEWS = [
  {
    name: "Marco F.",
    role: "Ristorante, Siena",
    text: "Servizio impeccabile. Hanno trovato 3 criticità che non sapevamo di avere. Manuale rifatto in tempi rapidi.",
    avatar: "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=160&h=160&fit=crop",
  },
  {
    name: "Giulia R.",
    role: "Agriturismo, Valdichiana",
    text: "Persone serie, del territorio. Ci seguono per HACCP, sicurezza e formazione: tutto in un unico interlocutore.",
    avatar: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=160&h=160&fit=crop",
  },
  {
    name: "Andrea P.",
    role: "Bar, Arezzo",
    text: "Il check-up gratuito è stato davvero utile, niente vendite forzate. Ci hanno spiegato cosa sistemare e perché.",
    avatar: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=160&h=160&fit=crop",
  },
];

const BUSINESS_TYPES = [
  { value: "Bar", icon: Wine },
  { value: "Ristorante", icon: Utensils },
  { value: "Agriturismo", icon: ChefHat },
  { value: "Hotel", icon: Hotel },
  { value: "Alimentari", icon: ShoppingBasket },
  { value: "Altro", icon: Building2 },
];

const HERO_IMG =
  "https://customer-assets.emergentagent.com/job_checkup-gratis/artifacts/1jnsnpoi_2.png";
const CLIPBOARD_IMG =
  "https://images.unsplash.com/photo-1523705480679-b5d0cc17a656?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTF8MHwxfHNlYXJjaHwyfHxjaGVja2luZyUyMGZvb2QlMjB0ZW1wZXJhdHVyZSUyMGNsaXBib2FyZHxlbnwwfHx8fDE3NzkyMjY1NzJ8MA&ixlib=rb-4.1.0&q=85";
const HANDSHAKE_IMG =
  "https://images.unsplash.com/photo-1681505531034-8d67054e07f6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDZ8MHwxfHNlYXJjaHwyfHxjb25zdWx0YW50JTIwaGFuZHNoYWtlJTIwb2ZmaWNlfGVufDB8fHx8MTc3OTIyNjU3Mnww&ixlib=rb-4.1.0&q=85";

const scrollToForm = () => {
  const el = document.getElementById("form");
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

const Section = ({ id, className = "", children }) => (
  <section id={id} className={`relative py-20 sm:py-24 ${className}`}>
    {children}
  </section>
);

const SectionLabel = ({ children, tone = "blue" }) => (
  <span
    className={`inline-flex items-center gap-2 rounded-full px-4 py-1.5 text-[11px] font-semibold uppercase tracking-[0.18em] ${
      tone === "yellow"
        ? "bg-amber-100 text-amber-900 ring-1 ring-amber-200"
        : "bg-blue-50 text-[#1E3A8A] ring-1 ring-blue-100"
    }`}
  >
    <Sparkles className="h-3.5 w-3.5" /> {children}
  </span>
);

const TopBar = () => (
  <div className="relative z-30 border-b border-blue-900/10 bg-[#1E3A8A] text-amber-50">
    <div className="mx-auto flex max-w-7xl items-center justify-between px-5 py-3 text-xs sm:text-sm">
      <div className="flex items-center gap-2 font-medium">
        <ShieldCheck className="h-4 w-4 text-amber-300" />
        <span>Centro Sicurezza Consulenze</span>
        <span className="hidden text-amber-300/70 sm:inline">·</span>
        <span className="hidden text-amber-100/80 sm:inline">Siena · Arezzo · Valdichiana</span>
      </div>
      <a
        href="#form"
        onClick={(e) => {
          e.preventDefault();
          scrollToForm();
        }}
        className="hidden items-center gap-1.5 rounded-full bg-amber-400 px-3.5 py-1.5 text-xs font-bold text-[#1E3A8A] transition hover:bg-amber-300 sm:inline-flex"
        data-testid="topbar-cta"
      >
        Prenota il check gratuito <ArrowRight className="h-3.5 w-3.5" />
      </a>
    </div>
  </div>
);

const Hero = () => (
  <Section id="hero" className="overflow-hidden pt-14 sm:pt-20">
    {/* Decorative shapes */}
    <div className="pointer-events-none absolute -top-20 -right-20 h-72 w-72 rounded-full bg-amber-200/40 blur-3xl" />
    <div className="pointer-events-none absolute -bottom-24 -left-10 h-72 w-72 rounded-full bg-blue-200/40 blur-3xl" />

    <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-12 px-5 sm:px-6 lg:grid-cols-12 lg:gap-10">
      <div className="lg:col-span-7">
        <SectionLabel>Check-up Food Gratuito</SectionLabel>
        <h1 className="mt-5 font-display text-[2.4rem] font-extrabold leading-[1.03] text-[#1E3A8A] sm:text-5xl lg:text-6xl">
          Se domani arrivasse un controllo,{" "}
          <span className="relative inline-block">
            <span className="relative z-10">la tua attività</span>
            <span className="absolute inset-x-0 bottom-1 z-0 h-3 bg-amber-300/70 sm:h-4" />
          </span>{" "}
          sarebbe davvero in regola?
        </h1>
        <p className="mt-6 max-w-xl text-base text-stone-700 sm:text-lg">
          Check-up gratuito per <strong>bar, ristoranti, agriturismi, hotel</strong> e attività
          alimentari della provincia di <strong>Siena, Arezzo e Valdichiana</strong>.
        </p>

        <div className="mt-8 flex flex-col items-start gap-4 sm:flex-row sm:items-center">
          <Button
            onClick={scrollToForm}
            size="lg"
            data-testid="hero-cta-btn"
            className="pulse-ring h-14 rounded-full bg-amber-400 px-8 text-base font-bold text-[#1E3A8A] shadow-[0_10px_30px_-12px_rgba(245,158,11,0.6)] transition hover:-translate-y-0.5 hover:bg-amber-300"
          >
            Prenota il check gratuito
            <ArrowRight className="ml-1 h-5 w-5" />
          </Button>
          <div className="flex items-center gap-2 text-sm text-stone-600">
            <CheckCircle2 className="h-4 w-4 text-emerald-600" />
            Nessun impegno · risposta entro 24 h
          </div>
        </div>

        <div className="mt-10 flex flex-wrap items-center gap-x-7 gap-y-3 text-xs font-semibold uppercase tracking-wider text-stone-500">
          <div className="flex items-center gap-1.5">
            <Star className="h-3.5 w-3.5 fill-amber-400 text-amber-400" />
            <Star className="h-3.5 w-3.5 fill-amber-400 text-amber-400" />
            <Star className="h-3.5 w-3.5 fill-amber-400 text-amber-400" />
            <Star className="h-3.5 w-3.5 fill-amber-400 text-amber-400" />
            <Star className="h-3.5 w-3.5 fill-amber-400 text-amber-400" />
            <span className="ml-1 text-stone-700">Clienti del territorio</span>
          </div>
          <div className="flex items-center gap-1.5">
            <ShieldCheck className="h-4 w-4 text-[#1E3A8A]" /> Specialisti HACCP
          </div>
          <div className="flex items-center gap-1.5">
            <Gift className="h-4 w-4 text-amber-600" /> Kit 2026 in omaggio
          </div>
        </div>
      </div>

      {/* Right visual */}
      <div className="relative lg:col-span-5">
        <div className="relative animate-float-slow">
          <div className="absolute -inset-3 rounded-[28px] bg-gradient-to-br from-amber-300 to-amber-500" />
          <img
            src={HERO_IMG}
            alt="Cucina professionale di ristorante"
            className="relative w-full rounded-[24px] object-cover shadow-2xl"
            style={{ height: "min(560px, 70vh)" }}
          />
        </div>

        <Card className="absolute -bottom-6 -left-4 max-w-[260px] rounded-2xl border-stone-200 bg-white p-4 shadow-xl sm:-left-8">
          <div className="flex items-center gap-3">
            <div className="grid h-11 w-11 place-items-center rounded-xl bg-emerald-50 text-emerald-700">
              <CheckCircle2 className="h-5 w-5" />
            </div>
            <div>
              <p className="font-display text-sm font-bold text-stone-900">Solo 20 minuti</p>
              <p className="text-xs text-stone-600">Analisi documenti & formazione</p>
            </div>
          </div>
        </Card>

        <Card className="absolute -top-4 -right-3 hidden max-w-[230px] rounded-2xl border-stone-200 bg-white p-4 shadow-xl sm:-right-6 sm:block">
          <div className="flex items-center gap-3">
            <div className="grid h-11 w-11 place-items-center rounded-xl bg-amber-100 text-amber-700">
              <Gift className="h-5 w-5" />
            </div>
            <div>
              <p className="font-display text-sm font-bold text-stone-900">Kit Food 2026</p>
              <p className="text-xs text-stone-600">In omaggio per te</p>
            </div>
          </div>
        </Card>
      </div>
    </div>

    {/* Ticker bar */}
    <div className="relative mt-16 overflow-hidden border-y border-blue-100 bg-blue-50/60 py-3">
      <div className="flex w-max animate-marquee gap-12 px-8 text-sm font-semibold uppercase tracking-wider text-[#1E3A8A]">
        {Array.from({ length: 2 }).map((_, i) => (
          <div key={i} className="flex items-center gap-12">
            <span>HACCP</span><span>·</span>
            <span>Allergeni</span><span>·</span>
            <span>Sicurezza Lavoro</span><span>·</span>
            <span>Formazione Alimentaristi</span><span>·</span>
            <span>Medicina del Lavoro</span><span>·</span>
            <span>Prevenzione Incendi</span><span>·</span>
          </div>
        ))}
      </div>
    </div>
  </Section>
);

const Problems = () => (
  <Section id="problems" className="bg-[#FAFAF9]">
    <div className="mx-auto max-w-7xl px-5 sm:px-6">
      <div className="max-w-3xl">
        <SectionLabel tone="yellow">I rischi reali</SectionLabel>
        <h2 className="mt-5 font-display text-3xl font-extrabold leading-tight text-stone-900 sm:text-4xl lg:text-5xl">
          Gli errori più comuni che troviamo{" "}
          <span className="text-[#1E3A8A]">nelle attività food</span>
        </h2>
      </div>

      <div className="mt-12 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {PROBLEMS.map(({ icon: Icon, text }, i) => (
          <Card
            key={i}
            data-testid={`problem-card-${i}`}
            className="group relative overflow-hidden rounded-2xl border-stone-200 bg-white p-6 transition hover:-translate-y-1 hover:shadow-lg"
          >
            <div className="flex items-start gap-4">
              <div className="grid h-12 w-12 shrink-0 place-items-center rounded-xl bg-amber-100 text-amber-700 transition group-hover:bg-amber-400 group-hover:text-[#1E3A8A]">
                <Icon className="h-5 w-5" />
              </div>
              <p className="pt-1.5 text-base font-medium text-stone-800">{text}</p>
            </div>
            <span className="pointer-events-none absolute -bottom-12 -right-12 h-32 w-32 rounded-full bg-amber-50 opacity-0 transition group-hover:opacity-100" />
          </Card>
        ))}
      </div>

      <div className="mt-12 rounded-3xl border border-blue-900/10 bg-[#1E3A8A] p-8 text-amber-50 sm:p-10">
        <div className="flex flex-col items-start justify-between gap-6 sm:flex-row sm:items-center">
          <p className="font-display text-2xl font-bold leading-snug sm:text-3xl">
            Molte attività pensano di essere in regola.{" "}
            <span className="text-amber-300">Fino al giorno del controllo.</span>
          </p>
          <Button
            onClick={scrollToForm}
            data-testid="problems-cta-btn"
            className="h-12 shrink-0 rounded-full bg-amber-400 px-6 font-bold text-[#1E3A8A] hover:bg-amber-300"
          >
            Verifica gratis la tua
          </Button>
        </div>
      </div>
    </div>
  </Section>
);

const Included = () => (
  <Section id="included">
    <div className="mx-auto grid max-w-7xl grid-cols-1 gap-12 px-5 sm:px-6 lg:grid-cols-12 lg:gap-16">
      <div className="lg:col-span-5">
        <SectionLabel>Cosa comprende</SectionLabel>
        <h2 className="mt-5 font-display text-3xl font-extrabold leading-tight text-stone-900 sm:text-4xl lg:text-5xl">
          Cosa comprende il <span className="text-[#1E3A8A]">Check-up Food</span> gratuito
        </h2>
        <p className="mt-5 text-base leading-relaxed text-stone-700 sm:text-lg">
          In circa <strong>20 minuti</strong> analizziamo la situazione della tua attività e
          individuiamo eventuali <strong>criticità documentali, formative o operative</strong>.
        </p>

        <div className="relative mt-10 hidden lg:block">
          <div className="absolute -inset-3 rounded-3xl bg-gradient-to-br from-blue-100 to-amber-100" />
          <img
            src={CLIPBOARD_IMG}
            alt="Controllo HACCP con clipboard"
            className="relative h-[360px] w-full rounded-2xl object-cover shadow-xl"
          />
        </div>
      </div>

      <div className="lg:col-span-7">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          {INCLUDED.map((item, i) => (
            <div
              key={i}
              data-testid={`included-item-${i}`}
              className="flex items-start gap-3 rounded-2xl border border-stone-200 bg-white p-5 transition hover:border-amber-300 hover:bg-amber-50/40"
            >
              <div className="grid h-7 w-7 shrink-0 place-items-center rounded-md bg-[#1E3A8A] text-amber-300">
                <CheckCircle2 className="h-4 w-4" />
              </div>
              <p className="text-sm font-semibold leading-snug text-stone-900 sm:text-base">
                {item}
              </p>
            </div>
          ))}
        </div>

        <div className="mt-8 rounded-2xl border border-amber-200 bg-amber-50 p-5 text-sm text-amber-900">
          <strong className="font-display text-base">Nessun impegno commerciale.</strong> Il
          check-up serve a fotografare la situazione reale. Decidi tu se proseguire, e con chi.
        </div>
      </div>
    </div>
  </Section>
);

const GiftSection = () => (
  <Section id="gift" className="bg-gradient-to-br from-[#1E3A8A] to-[#172554] text-amber-50">
    <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-12 px-5 sm:px-6 lg:grid-cols-12">
      <div className="lg:col-span-7">
        <span className="inline-flex items-center gap-2 rounded-full bg-amber-400/15 px-4 py-1.5 text-[11px] font-semibold uppercase tracking-[0.2em] text-amber-300 ring-1 ring-amber-400/30">
          <Gift className="h-3.5 w-3.5" /> In omaggio
        </span>
        <h2 className="mt-5 font-display text-3xl font-extrabold leading-tight sm:text-4xl lg:text-5xl">
          Il <span className="text-amber-300">Kit Controllo Food 2026</span>
        </h2>
        <p className="mt-5 max-w-xl text-base text-amber-50/85 sm:text-lg">
          Chi richiede il check-up riceverà anche un kit pratico per prepararsi meglio a un
          controllo.
        </p>

        <div className="mt-8 grid grid-cols-1 gap-3 sm:grid-cols-2">
          {GIFT_ITEMS.map(({ icon: Icon, text }, i) => (
            <div
              key={i}
              className="flex items-center gap-3 rounded-2xl border border-amber-400/15 bg-white/5 p-4 backdrop-blur"
            >
              <div className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-amber-400 text-[#1E3A8A]">
                <Icon className="h-4.5 w-4.5" />
              </div>
              <p className="text-sm font-semibold text-amber-50">{text}</p>
            </div>
          ))}
        </div>

        <Button
          onClick={scrollToForm}
          data-testid="gift-cta-btn"
          className="mt-9 h-14 rounded-full bg-amber-400 px-8 text-base font-bold text-[#1E3A8A] shadow-xl transition hover:-translate-y-0.5 hover:bg-amber-300"
        >
          Voglio il kit + il check gratuito <ArrowRight className="ml-1 h-5 w-5" />
        </Button>
      </div>

      <div className="relative lg:col-span-5">
        <div className="relative mx-auto max-w-md rotate-1 transition hover:rotate-0">
          <div className="absolute -inset-2 rounded-3xl bg-amber-400/30 blur-xl" />
          <div className="relative overflow-hidden rounded-3xl bg-amber-50 text-stone-900 shadow-2xl">
            <div className="flex items-center justify-between border-b border-amber-200 bg-amber-100 px-6 py-3">
              <p className="font-display text-sm font-bold uppercase tracking-widest">
                Kit Food 2026
              </p>
              <Sparkles className="h-4 w-4 text-amber-700" />
            </div>
            <div className="space-y-3 p-6">
              {GIFT_ITEMS.map((g, i) => (
                <div key={i} className="flex items-center gap-3 border-b border-dashed border-amber-200 pb-2 last:border-none">
                  <span className="grid h-6 w-6 place-items-center rounded-full bg-[#1E3A8A] text-[10px] font-bold text-amber-300">
                    {i + 1}
                  </span>
                  <span className="text-sm font-medium">{g.text}</span>
                </div>
              ))}
            </div>
            <div className="bg-[#1E3A8A] px-6 py-3 text-center text-xs font-semibold uppercase tracking-widest text-amber-200">
              Valore stimato 120 € — Gratis con il check-up
            </div>
          </div>
        </div>
      </div>
    </div>
  </Section>
);

const FormSection = () => {
  const initial = {
    business_name: "",
    contact_name: "",
    phone: "",
    email: "",
    town: "",
    business_type: "",
    haccp_manual: "",
    notes: "",
  };
  const [data, setData] = useState(initial);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const setField = (k) => (e) =>
    setData((d) => ({ ...d, [k]: e?.target ? e.target.value : e }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;

    const required = ["business_name", "contact_name", "phone", "email", "town", "business_type", "haccp_manual"];
    for (const k of required) {
      if (!String(data[k] || "").trim()) {
        toast.error("Compila tutti i campi obbligatori.");
        return;
      }
    }
    if (!/^\S+@\S+\.\S+$/.test(data.email)) {
      toast.error("Inserisci una email valida.");
      return;
    }

    try {
      setLoading(true);
      const res = await axios.post(`${API}/leads`, data);
      if (res?.data?.id) {
        setDone(true);
        toast.success("Richiesta inviata! Ti ricontatteremo a breve.");
        setData(initial);
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Errore di invio. Riprova.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Section id="form" className="bg-[#FAFAF9]">
      <div className="mx-auto max-w-7xl px-5 sm:px-6">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-14">
          <div className="lg:col-span-5">
            <SectionLabel tone="yellow">Pochi minuti, zero impegno</SectionLabel>
            <h2 className="mt-5 font-display text-3xl font-extrabold leading-tight text-stone-900 sm:text-4xl lg:text-5xl">
              Richiedi ora il tuo <span className="text-[#1E3A8A]">Check-up gratuito</span>
            </h2>
            <p className="mt-5 text-base text-stone-700">
              Lasciaci i tuoi dati: un nostro consulente del territorio ti ricontatta per fissare la verifica e spedirti il <strong>Kit Food 2026</strong>.
            </p>

            <div className="mt-8 space-y-4">
              {[
                { icon: CheckCircle2, label: "Risposta entro 24 h lavorative" },
                { icon: CheckCircle2, label: "Consulente dedicato del territorio" },
                { icon: CheckCircle2, label: "Nessun obbligo di acquisto" },
              ].map(({ icon: I, label }, i) => (
                <div key={i} className="flex items-center gap-3 text-stone-700">
                  <I className="h-5 w-5 text-emerald-600" />
                  <span className="text-sm font-medium">{label}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="lg:col-span-7">
            <Card className="rounded-3xl border-stone-200 bg-white p-6 shadow-xl sm:p-9">
              {done ? (
                <div className="py-10 text-center" data-testid="form-success">
                  <div className="mx-auto grid h-16 w-16 place-items-center rounded-full bg-emerald-100 text-emerald-700">
                    <CheckCircle2 className="h-8 w-8" />
                  </div>
                  <h3 className="mt-5 font-display text-2xl font-bold text-stone-900">
                    Richiesta inviata!
                  </h3>
                  <p className="mt-2 text-stone-600">
                    Ti ricontatteremo entro 24 h lavorative per fissare il check-up e spedirti il Kit Food 2026.
                  </p>
                  <Button
                    onClick={() => setDone(false)}
                    className="mt-6 rounded-full bg-[#1E3A8A] text-amber-50 hover:bg-[#172554]"
                    data-testid="form-new-request-btn"
                  >
                    Invia un'altra richiesta
                  </Button>
                </div>
              ) : (
                <form onSubmit={onSubmit} className="space-y-5" data-testid="lead-form">
                  <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                    <Field label="Nome attività" icon={Building2}>
                      <Input
                        data-testid="input-business-name"
                        value={data.business_name}
                        onChange={setField("business_name")}
                        placeholder="Es. Trattoria del Pozzo"
                        className="h-12 rounded-xl"
                      />
                    </Field>
                    <Field label="Nome referente" icon={UserRound}>
                      <Input
                        data-testid="input-contact-name"
                        value={data.contact_name}
                        onChange={setField("contact_name")}
                        placeholder="Nome e cognome"
                        className="h-12 rounded-xl"
                      />
                    </Field>
                    <Field label="Telefono" icon={Phone}>
                      <Input
                        data-testid="input-phone"
                        type="tel"
                        value={data.phone}
                        onChange={setField("phone")}
                        placeholder="+39 ..."
                        className="h-12 rounded-xl"
                      />
                    </Field>
                    <Field label="Email" icon={Mail}>
                      <Input
                        data-testid="input-email"
                        type="email"
                        value={data.email}
                        onChange={setField("email")}
                        placeholder="nome@email.it"
                        className="h-12 rounded-xl"
                      />
                    </Field>
                    <Field label="Comune" icon={MapPin}>
                      <Input
                        data-testid="input-town"
                        value={data.town}
                        onChange={setField("town")}
                        placeholder="Es. Montepulciano"
                        className="h-12 rounded-xl"
                      />
                    </Field>
                    <Field label="Tipologia attività" icon={Utensils}>
                      <Select value={data.business_type} onValueChange={setField("business_type")}>
                        <SelectTrigger data-testid="select-business-type" className="h-12 rounded-xl">
                          <SelectValue placeholder="Seleziona..." />
                        </SelectTrigger>
                        <SelectContent>
                          {BUSINESS_TYPES.map((b) => (
                            <SelectItem key={b.value} value={b.value} data-testid={`option-business-${b.value}`}>
                              <span className="inline-flex items-center gap-2">
                                <b.icon className="h-4 w-4 text-amber-600" />
                                {b.value}
                              </span>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </Field>
                  </div>

                  <Field label="Hai già un manuale HACCP?" icon={FileText}>
                    <div className="grid grid-cols-3 gap-3">
                      {["Sì", "No", "Non so"].map((v) => {
                        const active = data.haccp_manual === v;
                        return (
                          <button
                            key={v}
                            type="button"
                            data-testid={`haccp-option-${v}`}
                            onClick={() => setField("haccp_manual")(v)}
                            className={`h-12 rounded-xl border text-sm font-semibold transition ${
                              active
                                ? "border-[#1E3A8A] bg-[#1E3A8A] text-amber-300"
                                : "border-stone-200 bg-white text-stone-700 hover:border-amber-300 hover:bg-amber-50"
                            }`}
                          >
                            {v}
                          </button>
                        );
                      })}
                    </div>
                  </Field>

                  <Button
                    type="submit"
                    disabled={loading}
                    data-testid="form-submit-btn"
                    className="h-14 w-full rounded-full bg-amber-400 text-base font-extrabold text-[#1E3A8A] shadow-lg transition hover:-translate-y-0.5 hover:bg-amber-300 disabled:opacity-70"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="mr-1 h-5 w-5 animate-spin" /> Invio in corso...
                      </>
                    ) : (
                      <>
                        Voglio il check gratuito <ArrowRight className="ml-1 h-5 w-5" />
                      </>
                    )}
                  </Button>

                  <p className="text-center text-xs text-stone-500">
                    Nessun impegno commerciale. Ti ricontatteremo per fissare la verifica.
                  </p>
                </form>
              )}
            </Card>
          </div>
        </div>
      </div>
    </Section>
  );
};

const Field = ({ label, icon: Icon, children }) => (
  <div className="space-y-2">
    <Label className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-stone-600">
      {Icon ? <Icon className="h-3.5 w-3.5 text-amber-600" /> : null}
      {label}
    </Label>
    {children}
  </div>
);

const Authority = () => (
  <Section id="authority" className="bg-[#FAFAF9]">
    <div className="mx-auto max-w-7xl px-5 sm:px-6">
      <div className="grid grid-cols-1 gap-12 lg:grid-cols-12">
        <div className="lg:col-span-5">
          <SectionLabel>Perché sceglierci</SectionLabel>
          <h2 className="mt-5 font-display text-3xl font-extrabold leading-tight text-stone-900 sm:text-4xl">
            Perché <span className="text-[#1E3A8A]">Centro Sicurezza Consulenze</span>
          </h2>
          <p className="mt-5 text-base text-stone-700">
            Affianchiamo le aziende del territorio nella gestione di <strong>HACCP, sicurezza
            sul lavoro, formazione, medicina del lavoro e prevenzione incendi</strong>. Un unico
            interlocutore, semplice e continuativo.
          </p>

          <div className="mt-7 flex flex-wrap gap-2">
            {[
              "Azienda del territorio",
              "Supporto continuativo",
              "Consulente dedicato",
              "Specialisti HACCP",
              "Hotel & Agriturismi",
            ].map((b) => (
              <span
                key={b}
                className="rounded-full border border-stone-200 bg-white px-3 py-1.5 text-xs font-semibold text-stone-700"
              >
                {b}
              </span>
            ))}
          </div>

          <div className="mt-8 inline-flex items-center gap-3 rounded-2xl border border-stone-200 bg-white px-5 py-3">
            <div className="grid h-11 w-11 place-items-center rounded-xl bg-[#1E3A8A] text-amber-300">
              <ShieldCheck className="h-5 w-5" />
            </div>
            <div>
              <p className="font-display text-base font-bold text-stone-900">
                Centro Sicurezza Consulenze
              </p>
              <p className="text-xs text-stone-500">Sede operativa · Toscana</p>
            </div>
          </div>
        </div>

        <div className="lg:col-span-7">
          <div className="relative">
            <div className="absolute -inset-3 rounded-3xl bg-gradient-to-br from-amber-200 to-amber-400" />
            <img
              src={HANDSHAKE_IMG}
              alt="Consulenti aziendali"
              className="relative h-72 w-full rounded-2xl object-cover shadow-xl sm:h-80"
            />
          </div>

          <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-3">
            {REVIEWS.map((r, i) => (
              <Card
                key={i}
                data-testid={`review-card-${i}`}
                className="rounded-2xl border-stone-200 bg-white p-5"
              >
                <div className="flex items-center gap-1">
                  {Array.from({ length: 5 }).map((_, j) => (
                    <Star key={j} className="h-3.5 w-3.5 fill-amber-400 text-amber-400" />
                  ))}
                </div>
                <p className="mt-3 text-sm leading-relaxed text-stone-700">"{r.text}"</p>
                <div className="mt-4 flex items-center gap-3">
                  <img
                    src={r.avatar}
                    alt={r.name}
                    className="h-9 w-9 rounded-full object-cover ring-2 ring-amber-200"
                  />
                  <div>
                    <p className="text-sm font-bold text-stone-900">{r.name}</p>
                    <p className="text-[11px] text-stone-500">{r.role}</p>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </div>
    </div>
  </Section>
);

const Footer = () => (
  <footer className="border-t border-stone-200 bg-[#1E3A8A] py-10 text-amber-50">
    <div className="mx-auto flex max-w-7xl flex-col items-center justify-between gap-4 px-5 text-center sm:flex-row sm:text-left">
      <div className="flex items-center gap-3">
        <ShieldCheck className="h-5 w-5 text-amber-300" />
        <span className="font-display text-sm font-bold uppercase tracking-widest">
          Centro Sicurezza Consulenze
        </span>
      </div>
      <p className="text-xs text-amber-100/70">
        © {new Date().getFullYear()} Centro Sicurezza Consulenze. Check-up Food gratuito ·
        Siena · Arezzo · Valdichiana
      </p>
    </div>
  </footer>
);

const MobileStickyCTA = () => (
  <div className="fixed bottom-4 left-4 right-4 z-40 lg:hidden">
    <Button
      onClick={scrollToForm}
      data-testid="mobile-sticky-cta"
      className="h-14 w-full rounded-full bg-amber-400 text-base font-extrabold text-[#1E3A8A] shadow-2xl hover:bg-amber-300"
    >
      Prenota il check gratuito <ArrowRight className="ml-1 h-5 w-5" />
    </Button>
  </div>
);

export default function LandingPage() {
  useEffect(() => {
    // ping health (silently)
    axios.get(`${API}/health`).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-[#FAFAF9] text-stone-900">
      <TopBar />
      <Hero />
      <Problems />
      <Included />
      <GiftSection />
      <FormSection />
      <Authority />
      <Footer />
      <MobileStickyCTA />
    </div>
  );
}
